import { EnumFlowReactivatedLockedAccount } from '@/context/Auth/constants';
import AuthClient, { getAccountInfo, getUser } from 'clients/AuthClient';
import { getFirst, isValid } from 'clients/Clients';
import { getBankAccountInfo, logoutY360Account } from 'clients/CustomerClient';
import LoadingScreen from 'components/organisms/LoadingScreen';
import { CUSTOMER_TAG, ENUMS_ERROR_ACC_CODE, ERROR_CODE_CONTENT, RETRY_NUMBER_GET_USER } from 'constants/Enums';
import { HTTP_STATUS } from 'constants/Enums/http';
import { PRODUCT_CATALOG, QUICK_ORDER } from 'constants/Paths';
import useLocalStorageState from 'hooks/useLocalStorageState';
import useModal from 'hooks/useModal';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AuthService, { setAuthCookies } from 'services/AuthService';
import { getSettingInWeb } from 'services/SettingService';
import UserService from 'services/UserService';
import { ACCESS_TOKEN, ACCESS_TOKEN_LONGLIVE, GENERAL_DOMAIN } from 'sysconfig';
import gtag from 'utils/gtag';
import { clearLocalStorage } from 'utils/localStorageUtils';
import NotifyUtils from 'utils/NotifyUtils';
import { redirectToCustomerDashboard } from 'utils/redirectToCustomerDashboard';
import { getSessionTokenClient } from 'utils/SessionUtils';
import { clearChainSessionState } from 'utils/ChainSessionUtils';
import { useStore } from 'zustand-lib/storeGlobal';
import useMobileV2 from 'zustand-lib/storeMobile';
import useChatGuest from 'zustand-lib/useChatGuest';

const AuthContext = createContext({ user: {} });

export const AuthProvider = ({
  children,
  isShowingLogin,
  defaultShowSignUpModal,
  tokenv1,
  token,
  redirectUrl,
  initDisplayProduct = '',
  initUser,
}) => {
  const [user, setUser] = useState(initUser);
  const [customerInfo, setCustomerInfo] = useState(initUser);
  const [isShowOcr, setShowOcr] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [flowReactivatedAccountAfterLogin, setFlowReactivatedAccountAfterLogin] = useState(null);
  const [blockedUserName, setBlockedUserName] = useState(null);
  const [auth, setAuth] = useState('');

  const router = useRouter();
  const { pathname } = router;

  const [isShowLogin, toggleLogin] = useModal(!initUser && isShowingLogin);
  const [isShowSignUp, toggleSignUpFunc] = useModal(defaultShowSignUpModal);
  const [isShowForgetPassword, toggleForgetPassword] = useModal();
  const [isShowChangePassword, toggleChangePassword] = useModal();
  const [isShowRegisterGuest, toggleRegisterGuest] = useModal(false);
  const [isShowForgotPassMess, toggleShowForgotPassMess] = useModal(false);
  const [isShowBlockAccountMess, toggleShowBlockAccountMess] = useModal(false);
  const [isShowNotFoundAccountMess, toggleShowNotFoundAccountMess, getValShowResponseNotFoundAccountMess] = useModal(false);
  const [isShowGuestExpiredTime, toggleShowGuestExpiredTime] = useModal();

  const [mobileDisplayProduct, setMobileDisplayProduct] = useLocalStorageState('isMobilePaging', initDisplayProduct);

  const isMobileV2 = useMobileV2((state) => state.isMobileV2());
  const removeGuestId = useChatGuest((state) => state.removeGuestId);
  const removeGuestInfo = useChatGuest((state) => state.removeGuestInfo);
  const logoutZustand = useStore((state) => state.logout);

  const hasToggled = useRef(false);

  const refresh = () => {
    setRefreshKey(Date.now());
  };

  // TODO:
  // change password
  useEffect(() => {
    setUser(initUser);
    setCustomerInfo(initUser);
    setIsAuthenticated(!!initUser);
    setIsLoading(false);

    const { account } = initUser || {};
    const { isTempPassword } = account || {};
    if (isTempPassword && !hasToggled.current) {
      toggleChangePassword();
      hasToggled.current = true;
    }
    // FOR LOG tele
    if (initUser && initUser?.customerID) {
      Cookies.set('customerId', initUser?.customerID);
    }
  }, [initUser]);

  const toggleSignUp = () => {
    if (!isShowSignUp) {
      gtag.register();
    }
    toggleSignUpFunc();
  };

  const handleChangeForget = useCallback(() => {
    toggleLogin();
    toggleForgetPassword();
  }, [toggleLogin, toggleForgetPassword]);

  const handleChangeSignIn = useCallback(() => {
    toggleSignUp();
    toggleLogin();

    if (isMobileV2) {
      toggleSignUp();
    }
  }, [toggleSignUp, toggleLogin]);

  const handleChangePassword = useCallback(() => {
    toggleChangePassword();
  }, [toggleChangePassword]);

  const handleChangeSignUp = useCallback(() => {
    toggleLogin();
    toggleSignUp();
  }, [toggleLogin, toggleSignUp]);

  const handleChangeRegisterGuest = useCallback(() => {
    toggleLogin();
    toggleRegisterGuest();
  }, [toggleLogin, toggleRegisterGuest]);

  // red customer
  const handleRedCustomer = async () => {
    const userRes = await getUser();
    if (isValid(userRes)) {
      const userInfo = getFirst(userRes);
      if (userInfo?.tags?.indexOf(CUSTOMER_TAG.BAN) >= 0 || userInfo?.level == 'LEVEL_BLACKLIST') {
        router.push('/block-account');
        return true;
      }
    }
    return false;
  };

  const removeCookies = () => {
    Cookies.remove(ACCESS_TOKEN);
    Cookies.remove(ACCESS_TOKEN, { domain: GENERAL_DOMAIN });
    Cookies.remove(ACCESS_TOKEN_LONGLIVE);
    Cookies.remove(ACCESS_TOKEN_LONGLIVE, { domain: GENERAL_DOMAIN });
    clearChainSessionState();
  };

  const getAccountWithoutCache = async () => {
    try {
      const res = await AuthClient.getAccount();

      if (isValid(res)) {
        const customerInfoData = getFirst(res);
        const [bankInfoRes, accountInfoRes] = await Promise.all([getBankAccountInfo(), getAccountInfo()]);
        customerInfoData.bank = getFirst(bankInfoRes);
        customerInfoData.session = getFirst(accountInfoRes)?.session || null;
        setCustomerInfo(customerInfoData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 18 Oct 2021 : thêm thông tin bank info vào customerInfo
  const getUserInfo = useCallback(async () => {
    try {
      const ss = getSessionTokenClient();
      if (!ss || ss.length === 0) return null;

      const res = await UserService.getAccount({}, RETRY_NUMBER_GET_USER);

      if (isValid(res)) {
        const customerInfoData = getFirst(res);
        const [bankInfoRes, accountInfoRes] = await Promise.all([getBankAccountInfo(), getAccountInfo()]);
        customerInfoData.bank = getFirst(bankInfoRes);
        customerInfoData.account = getFirst(accountInfoRes)?.account || null;
        customerInfoData.session = getFirst(accountInfoRes)?.session || null;
        setCustomerInfo(customerInfoData);
        return res;
      }
      if (res.status === HTTP_STATUS.Unauthorized) {
        removeCookies();
      }
    } catch (error) {
      console.error(error);
    }
    return null;
  }, []);

  const reloadDataCustomer = async () => {
    try {
      const res = await UserService.getAccount();
      if (isValid(res)) {
        const customerInfoData = getFirst(res);
        const [bankInfoRes, accountInfoRes] = await Promise.all([getBankAccountInfo(), getAccountInfo()]);
        customerInfoData.bank = getFirst(bankInfoRes);
        customerInfoData.account = getFirst(accountInfoRes)?.account || null;
        customerInfoData.session = getFirst(accountInfoRes)?.session || null;
        setCustomerInfo(customerInfoData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setInfoUser = (userInfo) => {
    setUser(userInfo);
    setIsAuthenticated(!!userInfo);
    setIsLoading(false);
  };

  const clearAllStorageData = () => {
    localStorage.removeItem('collectorProductSentArray');
    clearLocalStorage();
    localStorage.setItem('logout', new Date());
    window.location.replace('/');
  };

  const logout = (callback, options = {}) => {
    logoutY360Account();
    AuthClient.logout();

    const { clearAll = false } = options || {};
    setInfoUser(null);
    setAuthCookies({}, true);
    removeCookies();
    //
    if (logoutZustand) {
      logoutZustand();
    }

    //
    try {
      localStorage.removeItem('collectorProductSentArray');
      clearChainSessionState();
      if (clearAll) {
        clearAllStorageData();
      }
    } catch (error) {}
    // thêm nếu k có options clear sẽ k đá về trang chủ
    window.location.href = '/';
    // hidden display form (login, forget, changpass) when click logOut
    if (typeof callback === 'function') {
      return callback();
    }

    // redirect to mienbac.thuocsi.vn
    setTimeout(() => {
      window.location.reload(true);
    }, 1000);

    return false;
  };

  const loadUserFromCookies = useCallback(
    async (callback) => {
      const res = await getUserInfo();

      const userInfo = getFirst(res, null);

      if (userInfo && userInfo.isQuest) {
        const timeRemaining = new Date(userInfo.expireAt).getTime() - new Date().getTime();
        setTimeout(
          () =>
            logout(() => {
              if (router.pathname !== '/') {
                router.push('/home');
              }
              toggleShowGuestExpiredTime();
            }),
          timeRemaining,
        );
      } else {
        // redirect to mienbac.thuocsi.vn
        // window.location.href = DOMAIN T_TS;
      }

      // chi-thi-16
      if (userInfo) userInfo.settings = await getSettingInWeb({ user: userInfo });
      // end

      const cookiesValue = Cookies.get(ACCESS_TOKEN_LONGLIVE, { domain: GENERAL_DOMAIN });
      if (cookiesValue && cookiesValue.length > 0) {
        setAuthCookies({ bearerToken: cookiesValue }, true);
      }

      if (userInfo) userInfo.cookiesValue = cookiesValue;
      setInfoUser(userInfo);
      setIsLoading(false);
      if (callback && typeof callback === 'function') callback(userInfo);
    },
    [getUserInfo, setIsLoading],
  );

  function login(info, rememberMe, onSuccess = () => {}) {
    clearChainSessionState();
    setAuthCookies(info, rememberMe);
    loadUserFromCookies(onSuccess);
  }

  async function getCustomerInfo() {
    const customerRes = await UserService.getAccount();

    const info = getFirst(customerRes);

    let errorRes = null;
    if (info?.reasonCodeBan) errorRes = customerRes;

    return { info, error: errorRes };
  }

  const redirectFlowReactivatedAccount = (canSelfReactivateAccount) => {
    if (canSelfReactivateAccount) {
      setFlowReactivatedAccountAfterLogin(EnumFlowReactivatedLockedAccount.CAN_SELF_REACTIVATED_WITH_OTP);
      return;
    }

    //default case
    setFlowReactivatedAccountAfterLogin(EnumFlowReactivatedLockedAccount.MUST_CONTACT_CS);

    // If in the future, there are more cases that need to be handled, add here
  };

  const checkLockedAccountAfterLogin = async ({ info, error, username }) => {
    //false mean account is not locked => pass
    if (!error) return false;

    if (!error?.errorCode) return false;

    // check show block message in login modal
    // this line make side effect
    toggleShowBlockAccountMess(undefined, undefined, true);

    switch (error.errorCode) {
      // locked account flow
      case ENUMS_ERROR_ACC_CODE.CUSTOMER_DELETED:
      case ENUMS_ERROR_ACC_CODE.CUSTOMER_BLOCK:
        if (username !== blockedUserName) {
          toggleShowBlockAccountMess();
        }
        refresh();
        setAuth(info?.bearerToken);
        setBlockedUserName(username);
        setCustomerInfo({ ...info });

        // From Feb2025, flow reactivated account will have 2 cases
        // 1. Account is locked by customer service
        // 2. Account is locked automatically
        // This function handle cases above
        redirectFlowReactivatedAccount(info?.canSelfReactivateAccount);
        return true;

      default:
        toggleShowNotFoundAccountMess(error);
        return true;
    }
  };

  const handleLockedAccountForMissingSSOMigrate = async (errorCode) => {
    // list status account isn't migrated SSO
    if (![ENUMS_ERROR_ACC_CODE.CUSTOMER_BLOCK, ENUMS_ERROR_ACC_CODE.CUSTOMER_DELETED].includes(errorCode)) return;

    setFlowReactivatedAccountAfterLogin(EnumFlowReactivatedLockedAccount.MUST_CONTACT_CS);
    toggleShowBlockAccountMess(undefined, undefined, true);
  };

  function handleLogin({
    username,
    password,
    rememberMe = true,
    success,
    type = 'CUSTOMER',
    callback,
    withOtp,
    requestOTP,
    otp,
    requestOTPv2 = false,
    partnerName,
    identityId,
  }) {
    // tracking login action - thuannc 19Jul2023
    gtag.clickBtnSignInAction();
    const param = {
      username,
      password,
      type,
      remember: rememberMe,
      refUrl: router?.pathname || '/',
      redirectUrl: redirectUrl || '/',
      partnerName,
      identityId,
    };
    const paramOtp = {
      username,
      type,
      remember: rememberMe,
      refUrl: router?.pathname || '/',
      redirectUrl: redirectUrl || '/',
      requestOTP,
      otp,
      requestOTPv2,
      partnerName,
      identityId,
    };

    // action
    AuthService.login(withOtp ? paramOtp : param)
      .then(async (result) => {
        if (callback) callback(result);
        if (!isValid(result)) {
          const { errorCode, message } = result;

          // hiện thông báo nhập sai mật khẩu
          try {
            const isMessForgotPass = localStorage.getItem('isMessForgotPass');
            if (!isMessForgotPass && !withOtp) {
              toggleShowForgotPassMess();
              localStorage.setItem('isMessForgotPass', true);
            }
          } catch (e) {
            if (e === 'QUOTA_EXCEEDED_ERR') {
              console.error('Quota exceeded!');
            }
          }

          if (!withOtp) {
            switch (errorCode) {
              case ENUMS_ERROR_ACC_CODE.RATE_LIMIT_EXCEEDED:
                NotifyUtils.error(message);
                break;

              case ENUMS_ERROR_ACC_CODE.CUSTOMER_DELETED:
              case ENUMS_ERROR_ACC_CODE.CUSTOMER_BLOCK:
                //haven't migrated SSO yet
                await handleLockedAccountForMissingSSOMigrate(errorCode);
                break;

              default:
                NotifyUtils.error(ERROR_CODE_CONTENT[errorCode] || 'Tài khoản hoặc mật khẩu không đúng. Vui lòng kiểm tra lại.');
                break;
            }
          }

          return;
        }

        // NotifyUtils.success('Đăng nhập thành công');
        removeGuestId(); // remove guestID của khách vãng lai
        removeGuestInfo(); // remove thông tin của khách vãng lai
        const userInfo = getFirst(result);
        login(userInfo, rememberMe);

        const { info, error } = await getCustomerInfo();

        const isLocked = await checkLockedAccountAfterLogin({ info, error, username });

        if (isLocked) return;

        if (type !== 'GUEST') {
          if (isShowLogin) toggleLogin();
          if (isShowSignUp && isMobileV2) toggleSignUp();
          router.push(PRODUCT_CATALOG);
          return;
        }

        if (router.query.redirectUrl) {
          router.push(router.query.redirectUrl);
          return;
        }

        // callback
        if (success) {
          const isRedirectedToDashboard = await redirectToCustomerDashboard(info, router);
          if (isRedirectedToDashboard) return;

          // const { redirectUrl = null } = router.query || {};
          if (isShowLogin) toggleLogin();
          if (isShowSignUp && isMobileV2) toggleSignUp();

          // NOTE: Comment log login event FE
          // send event tracking monitor
          // const metadata = {
          //   ref_url: router?.pathname || '/',
          //   redirect_url: redirectUrl || '/',
          //   host: window?.location?.host || '',
          // };
          // MonitorUtils.sendLoginEvent(MONITORING_COLLECTOR_TYPE.LOGIN, metadata);
          if (router.pathname === '/' || router.pathname === '/home') {
            router.push(redirectUrl || PRODUCT_CATALOG);
            return; // de tranh reload
          }
          success();
          if (router?.pathname?.indexOf('product')) {
            router.reload();
            // router.push(redirectUrl || PRODUCTS_URL);
          }
        }
      })
      .catch(() => {
        NotifyUtils.error('Đã có lỗi xảy ra');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleAutoLogin = async ({ customerId, jwtKey, rememberMe = true }) => {
    const result = await AuthService.autoLogin({ customerId, jwt: jwtKey });
    if (isValid(result)) {
      removeGuestId(); // remove guestID của khách vãng lai
      removeGuestInfo(); // remove thông tin của khách vãng lai
      const userInfo = getFirst(result);
      login(userInfo, rememberMe);

      const customerRes = await UserService.getAccount();
      const info = getFirst(customerRes);
      setCustomerInfo(info);

      setTimeout(async () => {
        const isRedirectedToDashboard = await redirectToCustomerDashboard(info, router);
        if (isRedirectedToDashboard) return;
        if (router.pathname === '/' || router.pathname === '/home') {
          router.push(redirectUrl || PRODUCT_CATALOG);
          return;
        }
      }, 1500);
    } else {
      NotifyUtils.error(result?.message || 'Tự động đăng nhập không thành công');
    }
  };

  const handleRegisterGuest = (data, success) => {
    setIsLoading(true);
    AuthService.registerGuest(data)
      .then((result) => {
        if (!isValid(result)) {
          switch (result.errorCode) {
            case 'ACCOUNT_NOT_ACCEPTED':
              NotifyUtils.error('Tài khoản này đã được đăng ký dùng thử. Bạn không thể tiếp tục');
              toggleRegisterGuest(false);
              toggleLogin();
              break;
            default:
              NotifyUtils.error(result.message);
              toggleRegisterGuest(false);
              toggleLogin();
          }
          return;
        }
        const { phone } = getFirst(result);
        const userName = `guest${phone}`;
        handleLogin({ username: userName, password: `Guest${phone}`, type: 'GUEST' });
        setIsLoading(false);
        toggleRegisterGuest(false);
        // callback
        if (success) {
          success();
          if (router.pathname === '/') {
            router.push(QUICK_ORDER);
          }
        }
      })
      .catch(() => {
        NotifyUtils.error('Đã có lỗi xảy ra');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResetPassword = useCallback(async (data) => {
    const result = await AuthService.resetPassword(data);
    if (isValid(result)) {
      NotifyUtils.info(result.message);
    } else {
      NotifyUtils.error(result?.message || 'Đã có lỗi xảy ra');
    }
  }, []);

  // off check token
  useEffect(() => {
    // nếu không có user thì mới check token
    if (initUser === null) {
      if (token) {
        setAuthCookies({ bearerToken: token });
      }
      loadUserFromCookies(async (userInfo) => {
        if (token) {
          if (redirectUrl) {
            router.push(redirectUrl);
          } else if (router.pathname === '/conversations') {
            router.push(router.asPath);
          } else if (router.pathname === '/lucky-wheel') {
            router.push(router.asPath);
          } else {
            router.push(router.pathname);
          }
        }

        // nếu không có user thì check token
        if (tokenv1) {
          // redirect'
          const result = await AuthService.loginv1({ tokenv1 });

          if (isValid(result)) {
            NotifyUtils.info(result.message);
            login(getFirst(result), true);
          } else {
            logout();
            NotifyUtils.error(result?.message || 'Đã có lỗi xảy ra');
            setTimeout(() => {
              window.location.href = '/';
            }, 1500);
          }
        }
      });
    }
  }, [pathname, loadUserFromCookies, tokenv1, token, redirectUrl]);

  const handleChangeDisplayProduct = (e) => {
    setMobileDisplayProduct(e.target.checked);
  };

  function onSetShowOcr() {
    return setShowOcr(!isShowOcr);
  }

  const checkMappingKiotvietIDWithBuymedID = async (params) => {
    const resReference = await AuthService.accountReferenceKiotviet({
      customerId: params.customerId,
      partnerName: params.partnerName,
    });
    if (resReference.status === 'OK') {
      const resReferenceData = getFirst(resReference);
      const { partnerId } = resReferenceData;
      if (partnerId) {
        NotifyUtils.warn(`Tài khoản Buymed đã tồn tại liên kết với Kiotviet (Partner ID: ${partnerId})`, { autoClose: 5000 });
      }
    } else {
      const result = await AuthService.checkMappingKiotvietIDWithBuymedID(params);
      if (result.status === 'OK') {
        NotifyUtils.success('Tài khoản Buymed đã được liên kết với Kiotviet.', { autoClose: 5000 });
      } else {
        NotifyUtils.error('Lỗi mapping Kiotviet với Buymed', { autoClose: 5000 });
      }
    }
  };

  function handlePartnerLogin({
    username,
    password,
    rememberMe = true,
    type = 'CUSTOMER',
    callback,
    withOtp,
    requestOTP,
    otp,
    requestOTPv2 = false,
    partnerName,
    identityId,
    onLoginResult,
  }) {
    // tracking login action - thuannc 19Jul2023
    gtag.clickBtnSignInAction();
    const param = {
      username,
      password,
      type,
      remember: rememberMe,
      refUrl: router?.pathname || '/',
      redirectUrl: redirectUrl || '/',
      partnerName,
      identityId,
    };
    const paramOtp = {
      username,
      type,
      remember: rememberMe,
      refUrl: router?.pathname || '/',
      redirectUrl: redirectUrl || '/',
      requestOTP,
      otp,
      requestOTPv2,
      partnerName,
      identityId,
    };
    // action
    AuthService.login(withOtp ? paramOtp : param)
      .then(async (result) => {
        if (onLoginResult) onLoginResult(result);
        if (!isValid(result)) {
          const { errorCode, message } = result;

          // hiện thông báo nhập sai mật khẩu
          try {
            const isMessForgotPass = localStorage.getItem('isMessForgotPass');
            if (!isMessForgotPass && !withOtp) {
              toggleShowForgotPassMess();
              localStorage.setItem('isMessForgotPass', true);
            }
          } catch (e) {
            if (e === 'QUOTA_EXCEEDED_ERR') {
              console.error('Quota exceeded!');
            }
          }
          if (!withOtp) {
            switch (errorCode) {
              case ENUMS_ERROR_ACC_CODE.RATE_LIMIT_EXCEEDED:
                NotifyUtils.error(message);
                break;
              default:
                NotifyUtils.error(ERROR_CODE_CONTENT[errorCode] || 'Tài khoản hoặc mật khẩu không đúng. Vui lòng kiểm tra lại.');
                break;
            }
          }

          return;
        }

        const userInfo = getFirst(result);
        login(userInfo, rememberMe);

        const customerRes = await UserService.getAccount();
        const info = getFirst(customerRes);

        // callback
        if (typeof callback === 'function') {
          callback({ customerId: info.customerID });
        }
      })
      .catch(() => {
        NotifyUtils.error('Đã có lỗi xảy ra');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        customerInfo,
        isAuthenticated,
        login,
        handleLogin,
        handleRegisterGuest,
        logout,
        isLoading,
        isShowLogin,
        toggleLogin,
        isShowSignUp,
        toggleSignUp,
        isShowForgetPassword,
        toggleForgetPassword,
        isShowRegisterGuest,
        toggleRegisterGuest,
        isShowGuestExpiredTime,
        toggleShowGuestExpiredTime,
        isShowOcr,
        onSetShowOcr,
        handleChangeForget,
        handleChangeSignIn,
        handleChangeSignUp,
        handleChangeRegisterGuest,
        handleResetPassword,
        handleChangePassword,
        toggleChangePassword,
        isShowChangePassword,
        isShowForgotPassMess,
        toggleShowForgotPassMess,
        isShowBlockAccountMess,
        toggleShowBlockAccountMess,
        reloadDataCustomer,
        handleRedCustomer,
        mobileDisplayProduct,
        handleChangeDisplayProduct,
        isShowNotFoundAccountMess,
        toggleShowNotFoundAccountMess,
        getValShowResponseNotFoundAccountMess,
        blockedUserName,
        setBlockedUserName,
        auth,
        refresh,
        refreshKey,
        getAccountWithoutCache,
        checkMappingKiotvietIDWithBuymedID,
        handleAutoLogin,
        handlePartnerLogin,
        setCustomerInfo,
        setUser,
        flowReactivatedAccountAfterLogin,
        setFlowReactivatedAccountAfterLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => useContext(AuthContext);
const LoadingScreenComponent = <LoadingScreen />;

export const LoadingRoute = ({ children }) => {
  const { isLoading } = useAuth();
  if (isLoading) {
    return LoadingScreenComponent;
  }

  return children;
};
